/**
 * 通用 HTTP 请求客户端（基于 axios）
 *
 * 能力：
 * 1. 拦截器：直接使用 axios 原生 interceptors（client.interceptors.request/response.use）
 * 2. 统一注入 Authorization、lang、channel、apikey 等公共请求头
 * 3. 统一处理后端 { code, msg, data } 结构：code === 0 返回 data，否则抛 HttpError
 * 4. 统一错误出口：onError 回调 + App 内 Toast（可用 silent 关闭）
 * 5. 401 无感刷新：H5 通过 Bridge 发 unauthorized → App 静默刷新 Token →
 *    App 写入 localStorage 后发 tokenRefreshed 回调 → H5 从 localStorage 读新 Token 重试原请求
 *    - Token 由 App 维护，H5 只读不写 localStorage
 *    - 刷新期间新发起的请求先等待刷新完成，避免带旧 Token 再次 401
 *
 * Bridge 使用 @chieftrade/chief-bridge，由项目自行 createBridgeClient 后注入，
 * 公共库不创建 Client（避免同页多个 Client 重复处理入站消息）。
 */
import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import type {
  ShowToastPayload,
  UnauthorizedPayload,
  Unsubscribe,
} from "@chieftrade/chief-bridge";
import { isEmbedded } from "./chief-bridge";
import {
  LangEnums,
  StorageKeys,
  langEnumToLangHeader,
  storageModel,
} from "./storage-manager";

/* ------------------------------------------------------------------ */
/* 扩展 axios 请求配置                                                   */
/* ------------------------------------------------------------------ */

declare module "axios" {
  interface AxiosRequestConfig {
    /** true 时返回完整 { code, msg, data }，不按 code 判断成功 */
    raw?: boolean;
    /** true 时出错不弹 Toast（错误仍会抛出） */
    silent?: boolean;
    /** true 时 401 不走无感刷新 */
    skipAuthRefresh?: boolean;
    /**
     * GET 请求超时后是否自动重试一次，默认 true；
     * 设为 false 关闭。非 GET 请求不会重试（POST 等可能重复提交）
     */
    retryOnTimeout?: boolean;
    /** 内部使用：是否为刷新 Token 后的重试请求 */
    _retry?: boolean;
    /** 内部使用：是否已做过超时重试 */
    _timeoutRetried?: boolean;
    /** 内部使用：本次请求实际携带的 Token */
    _token?: string;
  }
}

/* ------------------------------------------------------------------ */
/* 类型定义                                                             */
/* ------------------------------------------------------------------ */

/** 后端统一返回结构 */
export interface ApiEnvelope<T = unknown> {
  code: number;
  msg: string;
  data?: T;
  /** 401 时后端返回：0 = Token 自动过期，1 = 被挤下线 */
  status?: number;
}

/**
 * 无感刷新所需的最小 Bridge 能力
 * 直接传入 createBridgeClient 创建的实例即可，IncomingEvents 需声明：
 *   tokenRefreshed: void
 * tokenRefreshed 只作为"刷新完成"信号，不读取 payload；新 Token 统一从 localStorage 读取
 */
export interface HttpBridge {
  unauthorized(payload: UnauthorizedPayload): void;
  showToast(payload: ShowToastPayload): void;
  on(eventName: "tokenRefreshed", listener: () => void): Unsubscribe;
}

export type HttpErrorType =
  | "network" // 网络异常
  | "timeout" // 超时
  | "abort" // 主动取消
  | "unauthorized" // 401 且无法刷新
  | "http" // 其他非 2xx
  | "business"; // HTTP 成功但 code !== successCode

export class HttpError<T = unknown> extends Error {
  constructor(
    message: string,
    public readonly type: HttpErrorType,
    public readonly config?: AxiosRequestConfig,
    /** HTTP 状态码，网络/超时错误为 0 */
    public readonly status: number = 0,
    /** 业务 code（后端返回时才有） */
    public readonly code?: number,
    /** 原始响应体 */
    public readonly data?: T,
    /** 原始错误（AxiosError 等） */
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = "HttpError";
  }
}

/** 判断是否触发无感刷新的上下文 */
export interface RefreshContext {
  status: number;
  code?: number;
  /** 后端返回的 status 字段（0 过期 / 1 挤下线） */
  authStatus?: number;
  config: AxiosRequestConfig;
}

export interface HttpClientOptions {
  /** 基础路径，如 "/chiefApi/customer-info-server" */
  baseURL?: string;
  /** 默认超时，默认 15000ms */
  timeout?: number;
  /** 默认附加的请求头 */
  headers?: Record<string, string>;
  /** 业务成功 code，默认 0 */
  successCode?: number;
  /**
   * 用于 unauthorized 通知、tokenRefreshed 监听和错误 Toast；
   * 不传则不做无感刷新，也不弹 Toast
   */
  bridge?: HttpBridge;
  /** 等待 App 回调 tokenRefreshed 的超时时间，默认 10000ms */
  refreshTimeout?: number;
  /**
   * 当前 App 是否支持 tokenRefreshed 回调，默认：运行在 App 内即视为支持
   * 旧版本 App 不会回调，建议按版本号判断，否则会白等 refreshTimeout
   */
  canWaitTokenRefreshed?: () => boolean;
  /**
   * 是否走无感刷新，默认：HTTP 401 且 code = -201 / 401 且 status = 0（Token 自动过期）
   * 被挤下线（status = 1）或 -202 不刷新，只通知 App 自行处理
   */
  shouldRefresh?: (ctx: RefreshContext) => boolean;
  /** 是否给该 URL 附加 Authorization，默认仅 /chiefApi 开头的内部接口 */
  shouldAttachAuth?: (url: string) => boolean;
  /** 统一错误回调（Toast 之前执行），可用于上报 */
  onError?: (error: HttpError) => void;
}

/* ------------------------------------------------------------------ */
/* 工具函数                                                             */
/* ------------------------------------------------------------------ */

const DEFAULT_TIMEOUT = 15_000;
const DEFAULT_REFRESH_TIMEOUT = 10_000;
/**
 * 等待刷新期间轮询 localStorage 的间隔，用于兜底 App 未回调 tokenRefreshed 的情况。
 * 正常情况下 tokenRefreshed 回调会先到，轮询只是兜底，300ms 足够，也减轻低端设备负担
 */
const TOKEN_POLL_INTERVAL = 300;

function defaultShouldRefresh(ctx: RefreshContext): boolean {
  // code: 401 兼容 chief-common-support 等服务（与旧 http.ts 的映射一致）
  return (
    ctx.status === 401 &&
    (ctx.code === -201 || ctx.code === 401) &&
    (ctx.authStatus ?? 0) === 0
  );
}

function defaultShouldAttachAuth(url: string): boolean {
  // Authorization 只附加到内部 API，防止第三方 URL（如 CDN）拿到用户 token
  return url.startsWith("/chiefApi");
}

/**
 * 每次都从 localStorage 读最新 Token（由 App 维护，H5 只读）
 * 不回退到 storageModel：它是页面加载时的快照，App 清空或更新 Token 后会过期
 */
function readAccessToken(): string {
  return localStorage.getItem(StorageKeys.accessToken) || "";
}

/** 拼出最终 URL，用于判断是否为内部接口 */
function resolveURL(config: AxiosRequestConfig): string {
  const url = config.url ?? "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${config.baseURL ?? ""}${url}`;
}

function isEnvelope(data: unknown): data is ApiEnvelope {
  return (
    typeof data === "object" &&
    data !== null &&
    "code" in data &&
    (typeof (data as ApiEnvelope).code === "number" ||
      typeof (data as ApiEnvelope).code === "string")
  );
}

/**
 * 业务 code / status 转数字。
 * 空值返回 undefined；非数字字符串（如 "abc"）也返回 undefined，并打印警告，
 * 否则调用方会把它当成「没有 code」而静默走错分支，排查时无迹可寻。
 */
function toNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const num = Number(value);
  if (Number.isNaN(num)) {
    console.warn("[http-client] 无法解析为数字的 code/status:", value);
    return undefined;
  }
  return num;
}

function isTimeoutError(error: AxiosError): boolean {
  return (
    error.code === AxiosError.ECONNABORTED ||
    error.code === AxiosError.ETIMEDOUT
  );
}

/** 超时后是否自动重试：仅幂等的 GET，且只重试一次 */
function canRetryOnTimeout(config: AxiosRequestConfig | undefined): boolean {
  return (
    !!config &&
    (config.method ?? "get").toUpperCase() === "GET" &&
    config.retryOnTimeout !== false &&
    !config._timeoutRetried
  );
}

/**
 * 业务错误、网络错误取其 message，其余返回空串，由调用方用通用文案兜底。
 * 例：showToast(httpErrorMessage(e) || $t("error_network"))
 */
export function httpErrorMessage(e: unknown): string {
  return e instanceof HttpError &&
    (e.type === "business" || e.type === "network")
    ? e.message
    : "";
}

/**
 * 请求失败降级为 undefined，并打印日志。
 *
 * 用来替代 `.catch(() => undefined)`：后者会把代码 bug（TypeError 等）一起吞掉，
 * 出问题时控制台什么都看不到。
 * 例：getActivityInfo({ silent: true }).catch(ignoreWithLog("getActivityInfo"))
 */
export function ignoreWithLog(label: string): (error: unknown) => undefined {
  return (error: unknown) => {
    console.error(`${label} failed:`, error);
    return undefined;
  };
}

/**
 * 页面是否需要自行提示该错误。
 * 401 由 App 统一处理（请求库已通过 bridge 通知），主动取消不是错误，两者都不该再弹提示。
 * 例：if (shouldNotifyHttpError(e)) showToast(httpErrorMessage(e) || $t("error_network"))
 */
export function shouldNotifyHttpError(e: unknown): boolean {
  return !(
    e instanceof HttpError &&
    (e.type === "unauthorized" || e.type === "abort")
  );
}

/** 把 AxiosError（非 401）转成 HttpError */
function fromAxiosError(error: AxiosError): HttpError {
  const config = error.config;
  if (axios.isCancel(error) || error.code === AxiosError.ERR_CANCELED) {
    return new HttpError(
      "请求已取消",
      "abort",
      config,
      0,
      undefined,
      undefined,
      error,
    );
  }
  if (isTimeoutError(error)) {
    return new HttpError(
      "请求超时",
      "timeout",
      config,
      0,
      undefined,
      undefined,
      error,
    );
  }
  const response = error.response;
  if (!response) {
    return new HttpError(
      "網路錯誤",
      "network",
      config,
      0,
      undefined,
      undefined,
      error,
    );
  }
  const envelope = isEnvelope(response.data) ? response.data : undefined;
  return new HttpError(
    envelope?.msg || `HTTP ${response.status}`,
    "http",
    config,
    response.status,
    toNumber(envelope?.code),
    response.data,
    error,
  );
}

/* ------------------------------------------------------------------ */
/* 客户端                                                               */
/* ------------------------------------------------------------------ */

export function createHttpClient(options: HttpClientOptions = {}) {
  const successCode = options.successCode ?? 0;
  const shouldRefresh = options.shouldRefresh ?? defaultShouldRefresh;
  const shouldAttachAuth = options.shouldAttachAuth ?? defaultShouldAttachAuth;
  const canWaitTokenRefreshed = options.canWaitTokenRefreshed ?? isEmbedded;
  const bridge = options.bridge;

  const instance: AxiosInstance = axios.create({
    baseURL: options.baseURL ?? "",
    timeout: options.timeout ?? DEFAULT_TIMEOUT,
    headers: { Accept: "application/json", ...options.headers },
  });

  /* -------------------------- 无感刷新 -------------------------- */

  /** 进行中的刷新：并发 401 共用同一次刷新；刷新期间新请求也等待它 */
  let refreshing: Promise<void> | null = null;

  /**
   * 通知 App 刷新 Token，并等待刷新完成
   * @param failedToken 401 请求实际携带的 Token，用于判断 localStorage 中的 Token 是否已更新
   *
   * 完成判定（任一满足即视为成功）：
   * 1. 收到 App 的 tokenRefreshed 回调，且 localStorage 有 Token
   * 2. 轮询发现 localStorage 的 Token 已变化（兜底：App 写入了新 Token 但回调丢失/格式不符/
   *    被其他 __onChiefBridgeMessage 覆盖）
   * 3. 超时时 Token 已变化
   */
  function refreshToken(failedToken: string): Promise<void> {
    if (refreshing) return refreshing;
    if (!bridge) return Promise.reject(new Error("bridge 未配置"));
    const currentBridge = bridge;

    // pendingRef 在 Promise 创建后才赋值：executor 里同步执行 finish 时它仍为 null
    let finished = false;
    let pendingRef: Promise<void> | null = null;
    const pending = new Promise<void>((resolve, reject) => {
      let settled = false;
      let unsubscribe: Unsubscribe = () => {};
      const tokenChanged = () => {
        const latest = readAccessToken();
        return !!latest && latest !== failedToken;
      };

      const timer = window.setTimeout(() => {
        if (tokenChanged()) {
          finish(() => resolve());
          return;
        }
        finish(() => reject(new Error("等待 App 刷新 Token 超时")));
      }, options.refreshTimeout ?? DEFAULT_REFRESH_TIMEOUT);

      const poller = window.setInterval(() => {
        if (!tokenChanged()) return;
        console.info("[http-client] 检测到 Token 已更新，重试原请求");
        finish(() => resolve());
      }, TOKEN_POLL_INTERVAL);

      function finish(action: () => void): void {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        window.clearInterval(poller);
        unsubscribe();
        /*
         * 先 settle 再清 refreshing（不用 Promise.finally，兼容旧 WebView）：
         * 顺序颠倒会出现「refreshing 已为 null、Promise 尚未 settle」的中间态。
         * finished 供外层兜底：若 finish 在 executor 内同步执行（此时 refreshing 还没被赋值），
         * 赋值后要立刻清掉，否则 refreshing 会一直留着一个已结束的 Promise，之后再也不会刷新。
         */
        finished = true;
        action();
        if (pendingRef && refreshing === pendingRef) refreshing = null;
      }

      // 先订阅再通知，避免 App 回调过快丢消息
      // App 已把新 Token 写入 localStorage，这里只确认能读到，重试时由请求拦截器读取
      unsubscribe = currentBridge.on("tokenRefreshed", () => {
        console.info("[http-client] 收到 tokenRefreshed");
        if (!readAccessToken()) {
          finish(() => reject(new Error("App 未写入新 Token")));
          return;
        }
        finish(() => resolve());
      });

      console.info("[http-client] 401，通知 App 刷新 Token");
      currentBridge.unauthorized({ code: "-201", status: 0 });
    });

    pendingRef = pending;
    // 同步 settle 的情况下 finish 里的清理拿不到 pendingRef，这里兜底
    refreshing = finished ? null : pending;
    return pending;
  }

  /* ------------------------ 内置请求拦截器 ------------------------ */
  // 只在请求头缺失时补充，业务拦截器或单次请求设置的请求头优先，不依赖拦截器执行顺序

  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // App 正在刷新 Token 时，新请求先等待，避免带旧 Token 再 401；
      // 刷新失败/超时也放行，由响应拦截器兜底
      if (
        refreshing &&
        !config.skipAuthRefresh &&
        shouldAttachAuth(resolveURL(config))
      ) {
        await refreshing.catch(() => {});
      }
      const headers = AxiosHeaders.from(config.headers);
      const language = storageModel[StorageKeys.language] ?? LangEnums.zhHK;
      if (!headers.has("lang"))
        headers.set("lang", langEnumToLangHeader(language));
      const channel = storageModel[StorageKeys.channel];
      if (channel && !headers.has("channel")) headers.set("channel", channel);
      const apiKey = storageModel[StorageKeys.apiKey];
      if (apiKey && !headers.has("apikey")) headers.set("apikey", apiKey);

      const token = readAccessToken();
      if (
        token &&
        !headers.has("Authorization") &&
        shouldAttachAuth(resolveURL(config))
      ) {
        headers.set("Authorization", `Bearer ${token}`);
        config._token = token;
      }
      config.headers = headers;
      return config;
    },
  );

  /* ------------------------ 内置响应拦截器 ------------------------ */
  // axios 响应拦截器按注册顺序执行：内置拦截器在创建时注册，最先执行，
  // 业务拦截器拿到的是已处理过的结果（成功为 AxiosResponse，失败为 HttpError）

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const body: unknown = response.data;
      if (response.config.raw || !isEnvelope(body)) return response;
      const code = toNumber(body.code);
      if (code !== successCode) {
        return Promise.reject(
          new HttpError(
            body.msg || "请求失败",
            "business",
            response.config,
            response.status,
            code,
            body,
          ),
        );
      }
      return response;
    },
    async (error: unknown) => {
      if (error instanceof HttpError) throw error;
      if (!(error instanceof AxiosError)) {
        throw new HttpError(
          (error as Error)?.message || "请求失败",
          "network",
          undefined,
          0,
          undefined,
          undefined,
          error,
        );
      }

      const response = error.response;
      const config = error.config;

      // GET 超时自动重试一次（幂等请求）；重试请求仍会走完整的拦截器链
      if (
        !response &&
        config &&
        isTimeoutError(error) &&
        canRetryOnTimeout(config)
      ) {
        console.warn("[http-client] GET 请求超时，重试一次:", config.url);
        return instance.request({ ...config, _timeoutRetried: true });
      }

      if (!response || response.status !== 401 || !config) {
        throw fromAxiosError(error);
      }

      /* ---------- 401 处理 ---------- */
      const envelope = isEnvelope(response.data) ? response.data : undefined;
      const code = toNumber(envelope?.code);
      const authStatus = toNumber(envelope?.status);
      const unauthorizedError = (message?: string) =>
        new HttpError(
          message || envelope?.msg || "登录已失效",
          "unauthorized",
          config,
          401,
          code,
          response.data,
          error,
        );

      const canRefresh =
        !config._retry &&
        !config.skipAuthRefresh &&
        !!bridge &&
        canWaitTokenRefreshed() &&
        shouldRefresh({ status: 401, code, authStatus, config });

      if (canRefresh) {
        const headers = AxiosHeaders.from(config.headers);
        // 本次实际携带的 Token：内置拦截器注入的记在 _token，调用方自带的从 Authorization 解析
        const usedToken =
          config._token ??
          String(headers.get("Authorization") ?? "").replace(/^Bearer\s+/i, "");
        // 其他请求已刷新过 Token（本次用的是旧 Token），直接重试，不再触发刷新
        const latest = readAccessToken();
        if (!latest || latest === usedToken) {
          try {
            await refreshToken(usedToken);
          } catch (refreshError) {
            console.warn(
              "[http-client] Token 刷新失败，不再重试",
              refreshError,
            );
            throw unauthorizedError((refreshError as Error)?.message);
          }
        }
        // 去掉旧 Authorization，重试时由内置请求拦截器带上新 Token
        headers.delete("Authorization");
        return instance.request({
          ...config,
          headers,
          _retry: true,
          _token: undefined,
        });
      }

      // 不可刷新（挤下线、-202、刷新后重试仍 401 等）：按后端真实 code/status 通知 App 自行处理（如跳登录）
      // 重试请求带 _retry，不会再进入等待刷新流程，因此不会循环
      if (bridge && code !== undefined) {
        bridge.unauthorized({
          code: code === -202 ? "-202" : "-201",
          status: authStatus === 1 ? 1 : 0,
        });
      }
      throw unauthorizedError();
    },
  );

  /* -------------------------- 统一出口 -------------------------- */

  /** 统一错误处理：onError → Toast */
  function handleError(error: unknown, config: AxiosRequestConfig): never {
    const httpError =
      error instanceof HttpError
        ? error
        : error instanceof AxiosError
          ? fromAxiosError(error)
          : new HttpError(
              (error as Error)?.message || "请求失败",
              "network",
              config,
              0,
              undefined,
              undefined,
              error,
            );
    options.onError?.(httpError);
    // 401 由 App 处理（登录弹窗等），主动取消不提示
    // 优先读经过拦截器处理后的 config，拦截器里设置的 silent 也能生效
    const noToast =
      (httpError.config?.silent ?? config.silent) ||
      httpError.type === "unauthorized" ||
      httpError.type === "abort";
    if (!noToast && bridge && isEmbedded()) {
      bridge.showToast({ text: httpError.message, type: "error" });
    }
    throw httpError;
  }

  /**
   * 发起请求并返回业务数据
   * - 默认返回 envelope.data
   * - raw: true 返回完整响应体
   * - 响应体不是 { code, msg } 结构时原样返回
   */
  async function request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await instance.request(config);
      const body: unknown = response.data;
      // 读经过拦截器处理后的 config，与内置响应拦截器的 code 校验判断保持一致
      const raw = response.config?.raw ?? config.raw;
      if (raw || !isEnvelope(body)) return body as T;
      return body.data as T;
    } catch (error) {
      return handleError(error, config);
    }
  }

  type MethodConfig = Omit<AxiosRequestConfig, "url" | "method" | "data">;

  return {
    /** 底层 axios 实例，特殊场景直接使用（不经过 Toast/onError） */
    instance,
    /** axios 原生拦截器 */
    interceptors: instance.interceptors,
    request,
    get: <T = unknown>(url: string, config?: MethodConfig) =>
      request<T>({ ...config, url, method: "GET" }),
    delete: <T = unknown>(url: string, config?: MethodConfig) =>
      request<T>({ ...config, url, method: "DELETE" }),
    post: <T = unknown>(url: string, data?: unknown, config?: MethodConfig) =>
      request<T>({ ...config, url, method: "POST", data }),
    put: <T = unknown>(url: string, data?: unknown, config?: MethodConfig) =>
      request<T>({ ...config, url, method: "PUT", data }),
    patch: <T = unknown>(url: string, data?: unknown, config?: MethodConfig) =>
      request<T>({ ...config, url, method: "PATCH", data }),
  };
}

export type HttpClient = ReturnType<typeof createHttpClient>;
